/**
 * Firebase client SDK bootstrap for AVBP portal (rebuild — Sprint 1).
 *
 * Project: aayatan-veda-hr (shared with website + HR per 2026-04-18 lock).
 * These config values are PUBLIC Firebase "web app identifiers", not secrets.
 *
 * Auth model (LOCKED, Foundation 2026-05-13): Gmail + Apple sign-in via
 * Firebase Auth — NOT the old PIN / anonymous / OTP model. The pre-rebuild
 * anonymous+PIN bootstrap is retired with the rest of the old UI (see
 * src/_legacy/). Role resolution from the signed-in UID is Sprint-2+ wiring;
 * Sprint 1 ships the real OAuth handshake + a session role stub.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  type Auth,
} from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: 'AIzaSyCYfMyN0JX-kLXj9eLDJcR2IiOKH0kUzYk',
  authDomain: 'aayatan-veda-hr.firebaseapp.com',
  projectId: 'aayatan-veda-hr',
  storageBucket: 'aayatan-veda-hr.firebasestorage.app',
  messagingSenderId: '931110839645',
  appId: '1:931110839645:web:3d49fd9415ccd1168434b4',
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

/**
 * Firestore with a PERSISTENT local cache (IndexedDB).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  SLOW-LIST FIX 2026-05-21 (Anil) — owner reported the Clients / Projects
 *  / Partners lists taking a long time to populate.
 * ─────────────────────────────────────────────────────────────────────────
 *  Root cause: the rebuild used the bare `getFirestore(app)`. The modular
 *  Firestore SDK does NOT enable any local cache by default — so EVERY list
 *  screen mount re-fetched the entire `projects` collection (≈595 docs) and
 *  the `partners` collection from the SERVER over the network, cold, with
 *  nothing cached between mounts or across reloads. On a slow / high-latency
 *  connection that full-collection round-trip is the visible delay.
 *
 *  `persistentLocalCache` makes the SDK serve `onSnapshot` from IndexedDB
 *  FIRST (instant), then reconcile with the server in the background. The
 *  first ever load still hits the network once; every subsequent mount /
 *  reload is served from the local cache immediately. `persistentMultiple-
 *  TabManager` keeps that cache coherent across the portal + HR + Capture
 *  tabs the owner runs side by side.
 *
 *  Must use `initializeFirestore` (not `getFirestore`) — the cache option
 *  can only be set at initialisation, before any other Firestore call.
 * ─────────────────────────────────────────────────────────────────────────
 */
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
export const auth: Auth = getAuth(app);
export const storage: FirebaseStorage = getStorage(app);
/** Callable Functions client. Region pinned to match avbp-functions
 *  (asia-south1 — see avbp_gate_recheck.js REGION). */
export const functions: Functions = getFunctions(app, 'asia-south1');

/**
 * Local-only: route callables to the Functions emulator when the dev
 * server is started with VITE_FN_EMULATOR=1. Never active in a normal
 * `npm run dev` or any production build — the flag must be explicitly set.
 * Lets the owner test the real Claude `avbpChatExtract` locally without
 * deploying. (Anil's Round-2 emulator instructions omitted this wiring.)
 */
// Defensive read: under the Node test runner (_tsload.cjs esbuild transform)
// `import.meta.env` is undefined — a bare `.VITE_FN_EMULATOR` access there
// throws and takes down every test file that transitively imports firebase
// (Round-3 finding: this is the real cause of the pre-existing suite
// failures, not a logic regression). Optional-chain so test-time loading is
// safe; Vite still inlines the real env at build/dev time.
const FN_EMULATOR =
  typeof import.meta !== 'undefined' &&
  (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_FN_EMULATOR === '1';

if (FN_EMULATOR) {
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);

  console.info('[firebase] Functions → local emulator 127.0.0.1:5001');
}

/** Google (Gmail) provider — always prompt account chooser. */
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

/** Apple provider (Sign in with Apple via Firebase OAuth). */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');
